// The tower defence's numbers in one place: what a tower costs and what its upgrades do, what a
// kill pays, what a leak costs the Command Center, and which of the buildings and units from
// data.ts the mode uses at all. Tuned by sim/scripts/td-tune.ts.
import { DEFAULT_CONFIG, type BuildingDef, type GameConfig, type Threat } from './data.ts';
import { TICKS_PER_SECOND, TILE } from './fixed.ts';

/** The towers, in the order the shop shows them: cheap and general first, then the missile shield. */
export const TOWERS: readonly string[] = [
  'sentry', 'sniper_nest', 'land_mine', 'bunker', 'at_mine', 'atgm_post', 'howitzer', 'mortar', 'tomahawk', 'himars',
  'drone_interceptor', 'avenger', 'air_defence', 'gepard', 'c_ram', 'iron_beam', 'hpm', 'phalanx', 'missile_defence', 'patriot', 'thaad',
];

/** The units called in as support: which threats each hunts. */
export const PATROL_TARGETS: Record<string, readonly Threat[]> = {
  tank: ['infantry', 'vehicle'],
  attack_heli: ['infantry', 'vehicle', 'drone', 'aircraft'],
  predator: ['vehicle', 'infantry'],
  f16: ['aircraft', 'drone', 'cruise'],
};

/**
 * The fleet: the enemy's ships hold station this far out to sea, in tiles west of the coast, and
 * launch missiles and drones at the Command Center from there. Nothing the player builds reaches
 * them; what they launch is the fight. They sail in from FLEET_APPROACH tiles further out at the
 * start of their wave, and back out once their launchers are empty.
 */
export const FLEET_STATION = 20;
export const FLEET_APPROACH = 10;
/** A ship's speed on the way in and out, tiles a second: the first launch comes a few seconds into the wave. */
export const FLEET_SPEED_TPS = 4;
/** The fastest a patrol moves, units a tick: a fighter at full speed would never turn. */
export const PATROL_MAX_SPEED = Math.floor((6 * 1024) / TICKS_PER_SECOND);

/** The AC-130's guns: damage at the point of impact (half at the edge of its blast), blast radius, reload and time of flight. */
export type GunWeapon = '105' | '40' | '25';
export const GUN_WEAPONS: readonly GunWeapon[] = ['105', '40', '25'];
export const GUNSHIP_WEAPONS: Record<GunWeapon, { name: string; damage: number; radius: number; reloadTicks: number; flightTicks: number; air: boolean }> = {
  '105': { name: '105 mm', damage: 450, radius: Math.floor(TILE * 2.4), reloadTicks: 5 * TICKS_PER_SECOND, flightTicks: Math.round(1.2 * TICKS_PER_SECOND), air: false },
  '40': { name: '40 mm', damage: 140, radius: Math.floor(TILE * 1.1), reloadTicks: Math.round(0.8 * TICKS_PER_SECOND), flightTicks: Math.round(0.7 * TICKS_PER_SECOND), air: true },
  '25': { name: '25 mm', damage: 30, radius: Math.floor(TILE * 0.45), reloadTicks: 2, flightTicks: Math.round(0.4 * TICKS_PER_SECOND), air: true },
};

/** Cash to place each tower: dear, because a tower never breaks and never runs dry. */
export const PRICE: Record<string, number> = {
  sentry: 550, sniper_nest: 450, land_mine: 90, bunker: 875, at_mine: 175, atgm_post: 650, howitzer: 1050, mortar: 500, tomahawk: 2125, himars: 1625,
  drone_interceptor: 700, avenger: 950, air_defence: 1300, gepard: 1500, c_ram: 1125, iron_beam: 5000, hpm: 3200, phalanx: 900, missile_defence: 1300, patriot: 1875, thaad: 2500,
};

/** The enemy, in the order the waves bring them, and what each pays when it falls. */
export const REWARD: Record<string, number> = {
  conscript: 2, rifleman: 3, marksman: 5, heavy_gunner: 8, at_team: 10, tank: 40,
  scout_drone: 6, fpv_drone: 5, attack_drone: 10, shahed: 16, attack_heli: 55,
  cruise_missile: 30, ballistic: 60, hgv: 120,
  kalibr: 30, car_bomb: 25, kamikaze: 30,
  icbm: 250, hgv_nuclear: 180,
};

/** What a leak costs the Command Center's integrity: the warheads hurt the most, and a nuke nearly ends it. */
export const BREACH: Record<string, number> = {
  conscript: 1, rifleman: 1, marksman: 1, heavy_gunner: 2, at_team: 2, tank: 6,
  scout_drone: 1, fpv_drone: 1, attack_drone: 2, shahed: 4, attack_heli: 6,
  cruise_missile: 5, ballistic: 10, hgv: 12,
  kalibr: 5, car_bomb: 14, kamikaze: 8,
  icbm: 45, hgv_nuclear: 35,
};

export const MAX_TOWER_LEVEL = 3;

/** The upgrade's price as a share of the tower's price, by the level it goes to. */
const UPGRADE_PCT: Record<number, number> = { 2: 70, 3: 120 };
/** Damage and range gains by level, in percent over level 1. */
const DAMAGE_PCT: Record<number, number> = { 1: 0, 2: 35, 3: 80 };
const RANGE_PCT: Record<number, number> = { 1: 0, 2: 10, 3: 20 };
/** Sold towers give this much of what was spent on them back. */
export const SELL_PCT = 70;
/** A destroyed tower is rebuilt for this share of its price, back at its level. */
export const REBUILD_PCT = 50;

export function upgradePrice(type: string, toLevel: number): number {
  const path = TOWER_UPGRADES[type];
  if (path) return path[toLevel - 2]?.cost ?? 0;
  return Math.floor(((PRICE[type] ?? 0) * (UPGRADE_PCT[toLevel] ?? 0)) / 100);
}

/**
 * A tower's own upgrade path, in place of the generic three levels: each step has a name, a line,
 * its price and what it changes, and the steps stack. Index 0 is level 2.
 */
export interface TowerUpgrade {
  name: string;
  blurb: string;
  cost: number;
  burst?: number;
  burstGap?: number;
  attackTicks?: number;
  /** Range and damage over the tower's own, in percent. */
  rangePct?: number;
  damagePct?: number;
  targets?: readonly Threat[];
  incendiary?: { dmg: number; every: number; ticks: number };
  explosive?: { radius: number; pct: number };
}

export const TOWER_UPGRADES: Record<string, readonly TowerUpgrade[]> = {
  sentry: [
    { name: 'Incendiary Rounds', blurb: 'Every hit sets the target burning for three seconds. Orange tracers.', cost: 400,
      incendiary: { dmg: 2, every: 5, ticks: 3 * TICKS_PER_SECOND } },
    { name: 'Twin Guns', blurb: 'A second barrel on a bigger turret: two-thirds more rounds in every burst.', cost: 600,
      burst: 10, damagePct: 10 },
    { name: 'Explosive Rounds', blurb: 'Every hit bursts, catching whoever stands beside the target. Red tracers.', cost: 900,
      explosive: { radius: Math.floor(TILE * 0.7), pct: 50 }, damagePct: 20 },
    { name: 'Rotary Cannon', blurb: 'A six-barrel gatling with its own radar: a stream of fire, a quarter more reach, and it shoots down drones and helicopters too.', cost: 1400,
      burst: 18, burstGap: 1, rangePct: 25, damagePct: 30, targets: ['infantry', 'vehicle', 'ship', 'drone', 'aircraft'] },
  ],
};

/** The highest level a tower goes to: its own path's end, or the generic three. */
export function maxLevel(type: string): number {
  const path = TOWER_UPGRADES[type];
  return path ? path.length + 1 : MAX_TOWER_LEVEL;
}

const defCache = new Map<string, BuildingDef>();
/** A tower's definition at a level: for one with its own path, the steps up to it laid over its base. */
export function towerDefAt(type: string, level: number): BuildingDef {
  const base = TD_CONFIG.buildings[type]!;
  const path = TOWER_UPGRADES[type];
  if (!path || level <= 1) return base;
  const key = `${type}:${level}`;
  let def = defCache.get(key);
  if (def) return def;
  def = { ...base };
  let dmgPct = 0, rangePct = 0;
  for (const u of path.slice(0, level - 1)) {
    if (u.burst !== undefined) def.burst = u.burst;
    if (u.burstGap !== undefined) def.burstGap = u.burstGap;
    if (u.attackTicks !== undefined) def.attackTicks = u.attackTicks;
    if (u.targets) def.targets = [...u.targets];
    if (u.incendiary) def.incendiary = u.incendiary;
    if (u.explosive) def.explosive = u.explosive;
    dmgPct += u.damagePct ?? 0;
    rangePct += u.rangePct ?? 0;
    def.upgradeName = u.name;
  }
  def.damage = Math.floor(((base.damage ?? 0) * (100 + dmgPct)) / 100);
  def.range = Math.floor(((base.range ?? 0) * (100 + rangePct)) / 100);
  defCache.set(key, def);
  return def;
}

/** All the cash put into a tower at this level. */
export function spentOn(type: string, level: number): number {
  let total = PRICE[type] ?? 0;
  for (let l = 2; l <= level; l++) total += upgradePrice(type, l);
  return total;
}

export function sellValue(type: string, level: number): number {
  return Math.floor((spentOn(type, level) * SELL_PCT) / 100);
}

export function rebuildPrice(type: string): number {
  return Math.floor(((PRICE[type] ?? 0) * REBUILD_PCT) / 100);
}

export function damageAt(def: BuildingDef, level: number): number {
  // a tower on its own path has its gains in its definition already
  if (def.upgradeName !== undefined) return def.damage ?? 0;
  return Math.floor(((def.damage ?? 0) * (100 + (DAMAGE_PCT[level] ?? 0))) / 100);
}

export function rangeAt(def: BuildingDef, level: number): number {
  if (def.upgradeName !== undefined) return def.range ?? 0;
  return Math.floor(((def.range ?? 0) * (100 + (RANGE_PCT[level] ?? 0))) / 100);
}

/** The enemy walks faster here than in a raid: a road is not a battlefield. In percent. */
export const ENEMY_SPEED_PCT = 150;

/** Cash paid when a wave is cleared: a little more each wave. */
export function waveBonus(n: number): number {
  return 100 + 15 * n;
}

/** The enemy's fire against towers, in percent of what it would do in a raid: a nuisance that adds up, not a wrecking ball. */
export const TOWER_DAMAGE_PCT = 25;

/**
 * What walks or drives up the road is the easy part of a wave: a ground unit spawns with this share
 * of its raid hit points. The missiles and drones are what the money has to go on.
 */
export const GROUND_HP_PCT = 50;

/**
 * The heavy weapons against men and armour in the open, in percent of their listed damage: a
 * guided missile, a 155 mm shell, a Tomahawk's warhead, a tank's gun, a Hellfire. The machine gun
 * and the bunker chew; these wipe a group out.
 */
export const HEAVY_VS_GROUND_PCT: Record<string, number> = {
  atgm_post: 150, howitzer: 150, tomahawk: 150,
  tank: 150, attack_heli: 150, predator: 150,
};

/** A tower's or patrol's damage to a ground unit. */
export function groundDamage(type: string, damage: number): number {
  return Math.floor((damage * (HEAVY_VS_GROUND_PCT[type] ?? 100)) / 100);
}

/**
 * How wide an explosive weapon's blast is against men and armour on the ground, in units, at the
 * least: a shaped charge or a warhead that bursts against a vehicle still throws fragments across the
 * road. A gun's listed splash (what it does to a missile in the air) is for the air; on the ground it
 * is this if it is wider. Everyone in the circle takes it: full at the centre, half at the edge.
 */
export const GROUND_BLAST: Record<string, number> = {
  atgm_post: Math.floor(TILE * 1.5), howitzer: Math.floor(TILE * 2.5), tomahawk: Math.floor(TILE * 3.5),
  land_mine: TILE * 2, at_mine: TILE * 2,
  tank: Math.floor(TILE * 1.5), attack_heli: Math.floor(TILE * 1.5), predator: Math.floor(TILE * 1.5),
};

/** The blast radius of a tower's or patrol's hit on a ground unit: its own splash, or the ground floor for its type if that is wider; 0 for a bullet. */
export function groundBlast(type: string, splash: number | undefined): number {
  return Math.max(splash ?? 0, GROUND_BLAST[type] ?? 0);
}

/**
 * What a kill pays by the wave it came with, in percent. Full pay throughout: a taper past the
 * hand-made waves was tried after a played game ended with cash to spare, and it starved the
 * missile shield instead (the self-play builder lost Switchback and Pincer on wave 27).
 */
export function rewardPct(_n: number): number {
  return 100;
}

/** Calling the next wave while one is still on pays extra, for the player in a hurry. */
export const EARLY_CALL_BONUS_PCT = 50;

/** A wave is over this long after its last enemy is dead or through. */
export const WAVE_QUIET_TICKS = 2 * TICKS_PER_SECOND;

/** The game's rules: the raids' config as it stands; only the buildings in TOWERS and `hq` matter. */
export const TD_CONFIG: GameConfig = DEFAULT_CONFIG;
